import CelebrationOutlinedIcon from '@mui/icons-material/CelebrationOutlined';
import DoDisturbOnOutlinedIcon from '@mui/icons-material/DoDisturbOnOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Popover,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useGeoLocation } from '../contexts/GeoLocationContext';
import { useBillDataSheets } from '../hooks/useBillData';
import { stateLegislatorUrls } from '../utils/stateLegislatorUrls';

interface BillStatusStepperProps {
  billTitle: string;
  country?: string;
}

const statusConfig: Record<string, { icon: React.ReactNode; color: string }> = {
  'Not Started': {
    icon: <DoDisturbOnOutlinedIcon sx={{ fontSize: 28, color: 'error.main' }} aria-hidden="true" />,
    color: 'error.main',
  },
  'In Progress': {
    icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 28, color: 'warning.main' }} aria-hidden="true" />,
    color: 'warning.main',
  },
  Passed: {
    icon: <CelebrationOutlinedIcon sx={{ fontSize: 28, color: 'success.main' }} aria-hidden="true" />,
    color: 'success.main',
  },
};

const STATUS_STEPS = ['Not Started', 'In Progress', 'Passed'];

const BillStatusStepper: React.FC<BillStatusStepperProps> = ({ billTitle, country = 'united-states' }) => {
  const { location } = useGeoLocation();
  const { data, isLoading: loading } = useBillDataSheets();
  const [legislatorDialogOpen, setLegislatorDialogOpen] = useState(false);
  const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLButtonElement | null>(null);

  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    const calculateStep = () => {
      if (loading) {
        return 0;
      }
      if (!location?.state || !data || data.length === 0) {
        return 0;
      }

      const stateData = data.find(
        (item) =>
          item.state.toLowerCase() === location.state?.toLowerCase() &&
          item.billTitle.toLowerCase().replace(/[^a-z0-9]/g, '') === billTitle.toLowerCase().replace(/[^a-z0-9]/g, ''),
      );

      if (!stateData) {
        return 0;
      }

      if (!stateData.billStatus) {
        return 0;
      }

      const statusIndex = STATUS_STEPS.indexOf(stateData.billStatus);
      return statusIndex !== -1 ? statusIndex : 0;
    };

    const step = calculateStep();
    setCurrentStep(step);
    setInfoAnchorEl(null);
  }, [location, data, loading, billTitle]);

  const currentStatus = STATUS_STEPS[currentStep];

  const stateEntry = location?.state
    ? stateLegislatorUrls.find((entry) => entry.state.toLowerCase() === location.state?.toLowerCase())
    : undefined;
  const stateLegUrl = stateEntry?.stateLegUrl;

  const renderDescription = useCallback(() => {
    if (currentStatus === 'In Progress') {
      // Gate legislature link for non-US users
      if (country !== 'united-states') {
        return (
          <>
            This bill is advancing through legislation.{' '}
            <Link
              component="button"
              onClick={() => setLegislatorDialogOpen(true)}
              sx={{
                color: 'primary.main',
                textDecoration: 'underline',
                cursor: 'pointer',
                verticalAlign: 'baseline',
              }}
            >
              Legislature info
            </Link>
          </>
        );
      }
      const legislatureUrl = stateLegUrl || 'https://www.ncsl.org/about-state-legislatures/state-legislatures-websites';
      return (
        <>
          This bill is advancing through legislation. Check the{' '}
          <Link
            href={legislatureUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: 'primary.main',
              textDecoration: 'underline',
            }}
          >
            legislature's website
          </Link>{' '}
          for updates.
        </>
      );
    }
    if (currentStatus === 'Passed') {
      return 'The governor has signed this bill into law!';
    }
    return (
      <>
        An advocate is needed to assist in securing a legislative sponsor.{' '}
        <Link
          component={RouterLink}
          to="/join-the-team"
          sx={{
            color: 'primary.main',
            textDecoration: 'underline',
          }}
        >
          Join the movement
        </Link>{' '}
        to get this bill started in your state.
      </>
    );
  }, [currentStatus, country, stateLegUrl]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      {/* Status icon + label + info button inline */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        {statusConfig[currentStatus]?.icon ?? statusConfig['Not Started'].icon}
        <Typography component="p" sx={{ fontWeight: 700, color: statusConfig[currentStatus]?.color ?? 'error.main' }}>
          {currentStatus}
        </Typography>
        <IconButton
          size="small"
          aria-label="Bill status information"
          aria-controls={Boolean(infoAnchorEl) ? 'bill-status-info-popover' : undefined}
          aria-expanded={Boolean(infoAnchorEl)}
          onClick={(e) => setInfoAnchorEl(e.currentTarget)}
          sx={{ color: 'text.secondary', p: 0.25 }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Info popover — preserves interactive links */}
      <Popover
        id="bill-status-info-popover"
        open={Boolean(infoAnchorEl)}
        anchorEl={infoAnchorEl}
        onClose={() => setInfoAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Typography variant="body2" component="span" sx={{ display: 'block', p: 1.5, maxWidth: 280 }}>
          {renderDescription()}
        </Typography>
      </Popover>

      {/* Non-US legislature info dialog */}
      <Dialog open={legislatorDialogOpen} onClose={() => setLegislatorDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Legislature Information</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Legislator lookup is only available for U.S. users. For Canadian legislative information, please visit your
            provincial or territorial legislature's website directly.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLegislatorDialogOpen(false)} aria-label="Close legislature information dialog">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillStatusStepper;
